import React, { useState, useMemo, useEffect, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { Product, LinkedInstaller, Address } from "types";
import { Row } from "@tanstack/react-table";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { StatusBadge } from "components/Productlocationstatus";
import { ViewIconButton, EditIconButton, DeleteIconButton, CancelIconButton, UnlinkIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import imageCustomSquare from "assets/images/imagecustomrounded.png";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";
import { fetchMarketplaceProfilesSelect2 } from "api/postTypeApiCalls";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { glob } from "fs";
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";

interface ExportRow {
  "Agent Name": string;
  "Product Name": string;
  "SKU": string;
  "Quantity": number;
  "Brand": string;
  "Category": string;
  "Item Condition": string;
  "Status": string;
}

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

type AgentProduct = {
  _id: string;
  /** @deprecated Prefer agent_membership_id from agent-inventories datatable */
  agent_id?: string;
  agent_membership_id?: string;
  agent_global_user_id?: string;
  agent_org_id?: string;
  agent_name: string;
  agent_email: string;
  inventory_type: string;
  reference_product_id: string;
  linked_installer_details: LinkedInstaller;
  product: Product;
};

const getAgentMembershipId = (product: Pick<AgentProduct, "agent_membership_id" | "agent_id">) =>
  String(product.agent_membership_id || product.agent_id || "").trim();

type Supplier = {
  supplier_id: string;
  marketplace_profile_id?: string;
  organization_id?: string;
  name: string;
  email: string;
};

type SelectionState = {
  [productId: string]: string;
};

const columnHelper = createColumnHelper<AgentProduct>();
const HEX24 = /^[a-f0-9]{24}$/i;
const ABS_URL = /^https?:\/\//i;
const isHexId = (s?: string) => !!(s && HEX24.test(s.trim()));

const RealtorAgentInventory = () => {
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null); // Track deletion in progress
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const [toastShown, setToastShown] = useState(false);
  const [agentProducts, setAgentProducts] = useState<AgentProduct[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<AgentProduct[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';
  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.agent_inventory
  );
  const [showModal, setShowModal] = useState(false);
  const [linkedAgentId, setLinkedAgentId] = useState<string>("");
  const [linkedProductId, setLinkedProductId] = useState<string>("");
  const [linkedQuantity, setLinkedQuantity] = useState<number>(1);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const organizationId = getAuthCookie("organizationId") || "";
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const tableRef = useRef<HTMLTableElement>(null);


  const [supplierSelections, setSupplierSelections] = useState<SelectionState>(
    {}
  );
  const [addressSelections, setAddressSelections] = useState<SelectionState>(
    {}
  );
  const [dateSelections, setDateSelections] = useState<SelectionState>({});
  const [methodSelections, setMethodSelections] = useState<SelectionState>({});

  const [addressMap, setAddressMap] = useState<Record<string, Address[]>>({});
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const [deleteTarget, setDeleteTarget] = useState<{ agentId: string; productId: string; name: string } | null>(null);

  const allImageIds = useMemo(() => {
    const ids: string[] = [];
    for (const row of (agentProducts || [])) {
      const id = (row?.product as any)?.images?.[0]?.url as string;
      if (isHexId(id)) ids.push(id.trim());
    }
    return Array.from(new Set(ids));
  }, [agentProducts]);

  // hook expects: [{ product: { variants: [{ images: [{ url }] }] } }]
  const dataForMap = useMemo(() => {
    if (allImageIds.length === 0) return [];
    return [
      { product: { variants: [{ images: allImageIds.map(url => ({ url })) }] } }
    ];
  }, [allImageIds]);

  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);


  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(
          // `${process.env.REACT_APP_API_URL}/user/agent_inventory/datatable/${userId}`,
          `${process.env.REACT_APP_API_URL}/brokerage/organizations/${organizationId}/agent-inventories/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
              filters: {

              },
              sort: {
                field: "created_at",
                order: "desc",
              },
              // fields: ["firstName", "lastName", "email", "jobTitle", "warehouses", "status", "created_at"]
            })
          }
        );

        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }

        const result = await response.json();
        console.log("Fetched data:", result);

        if (result && Array.isArray(result?.data)) {
          setAgentProducts(result?.data);
          setRowCount(result?.total);
          setFilteredProducts(result?.data);
        } else {
          console.error("Unexpected API response structure:", data);
          setAgentProducts([]);
          setFilteredProducts([]);
        }
      } catch (err) {
        console.error("Error fetching products:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [token, userId, globalFilter, pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    const fetchSuppliers = async () => {
      if (!token) return;
      try {
        const { results } = await fetchMarketplaceProfilesSelect2(token, {
          category: "signage",
          page: 1,
          limit: 100,
        });
        const mapped: Supplier[] = (results || [])
          .map((r: any) => ({
            // POST linked-installer expects supplier_membership_id
            supplier_id: String(r.membership_id || "").trim(),
            marketplace_profile_id: String(r.id || "").trim(),
            organization_id: String(r.organization_id || "").trim(),
            name: String(r.text || r.id || "").trim(),
            email: "",
          }))
          .filter((s: Supplier) => s.supplier_id && s.name);
        setSuppliers(mapped);
      } catch (err) {
        console.error("Error fetching suppliers (marketplace profiles):", err);
      }
    };
    fetchSuppliers();
  }, [token]);

  const normalizeSupplierVendorId = (raw?: string) => {
    if (!raw) return "";
    const direct = suppliers.find((s) => s.supplier_id === raw);
    if (direct) return direct.supplier_id;
    const byProfile = suppliers.find((s) => s.marketplace_profile_id === raw);
    if (byProfile) return byProfile.supplier_id;
    const byOrg = suppliers.find((s) => s.organization_id === raw);
    return byOrg?.supplier_id || raw;
  };

  // const handleSubmitLink = async (e: React.FormEvent) => {
  //   e.preventDefault();

  //   console.log('linkedAgentId', linkedAgentId);
  //   console.log('linkedProductId', linkedProductId);
  //   console.log('selectedSupplier', selectedSupplier);

  //   if (!selectedSupplier || !linkedProductId) {
  //     alert("Please select a supplier and enter a valid product ID.");
  //     return;
  //   }

  //   try {
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/agent/${linkedAgentId}/inventory/${linkedProductId}/link`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //           Authorization: `Bearer ${token}`,
  //         },
  //         body: JSON.stringify({
  //           linked_mode: 'pending',
  //           linked_installer: {
  //             id: selectedSupplier,
  //             name: suppliers?.find((s) => s.supplier_id === selectedSupplier)
  //               ?.name,
  //             // state: "pending",
  //           },
  //           // reason:
  //           //   "Requesting to link this product with supplier for inventory management.",
  //         }),
  //       }
  //     );

  //     if (!response.ok) throw new Error("Failed to link product.");

  //     showSuccess("Product linked successfully!");
  //     handleCloseModal();
  //     navigate(0);
  //   } catch (error) {
  //     console.error("Error linking product:", error);
  //     showError("Failed to link product.");
  //   }
  // };

  const handleDelete = async (agentId: string, productId: string) => {
    setDeleting(productId); // Set the product being deleted

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/agent/${agentId}/inventory/${productId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete the product.");
      }

      showSuccess("Product deleted successfully!");
      // Update the state by removing the deleted product
      setAgentProducts((prev) =>
        prev?.filter((product) => product._id !== productId)
      );
      setDeleting(null); // Clear the deleting state
      // navigate(0);
    } catch (err) {
      showError("Failed to delete the product.");
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
      setDeleting(null); // Clear the deleting state
    }
  };

  const handleSupplierChange = (productId: string, value: string) => {
    setSupplierSelections((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleMethodChange = (
    productId: string,
    value: string,
    agentId: string
  ) => {
    setMethodSelections((prev) => ({
      ...prev,
      [productId]: value,
    }));

    // if (value === "pickup_from_location" || value === "at_installation_site") {
    //   fetchUserAddresses(agentId);
    // }
  };

  const handleAddressChange = (productId: string, value: string) => {
    setAddressSelections((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleDateChange = (productId: string, value: string) => {
    setDateSelections((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleDeleteClick = (product: AgentProduct) => {
    setDeleteTarget({
      agentId: getAgentMembershipId(product),
      productId: product._id,
      name: product.product?.name  || "Product",
    });
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
    setDeleting(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await handleDelete(deleteTarget.agentId, deleteTarget.productId);
    setDeleteTarget(null);
  };

  useEffect(() => {
    const fetchAllAgentAddresses = async () => {
      const uniqueMembershipIds = Array.from(
        new Set(
          (agentProducts || [])
            .map((p) => getAgentMembershipId(p))
            .filter(Boolean)
        )
      );

      for (const membershipId of uniqueMembershipIds) {
        if (addressMap[membershipId]) continue;
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/organization-memberships/${membershipId}/addresses`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          const data = await response.json();
          if (!response.ok || data?.error) {
            throw new Error(data?.error || data?.message || `Failed to fetch addresses for ${membershipId}`);
          }
          const list: Address[] = (Array.isArray(data)
            ? data
            : Array.isArray(data?.data)
              ? data.data
              : Array.isArray(data?.addresses)
                ? data.addresses
                : Array.isArray(data?.results)
                  ? data.results
                  : []
          ).map((row: any) => ({
            ...row,
            _id: String(row?._id ?? row?.id ?? ""),
          })).filter((row: Address) => row._id);

          setAddressMap((prev) => ({
            ...prev,
            [membershipId]: list,
          }));
        } catch (err) {
          console.error(
            `Failed to fetch addresses for agent membership ${membershipId}`,
            err
          );
        }
      }
    };
    if (agentProducts?.length > 0) {
      fetchAllAgentAddresses();
    }
  }, [agentProducts, token]);

  const handleSubmitLink = async (productId: string, membershipId: string) => {
    // Get the product from agentProducts
    const product = agentProducts?.find((p) => p._id === productId);

    // Get values from either state or fallback from product object
    const method =
      methodSelections[productId] ??
      product?.linked_installer_details?.product_location_status;

    const rawSupplierId =
      supplierSelections[productId] ??
      (product?.linked_installer_details as any)?.supplier_membership_id ??
      product?.linked_installer_details?.supplier_id;
    const supplierMembershipId = normalizeSupplierVendorId(rawSupplierId);

    const supplierName = suppliers?.find(
      (supplier) => supplier.supplier_id === supplierMembershipId
    )?.name;

    const dropOffDate = dateSelections[productId]
      ? new Date(dateSelections[productId]).toISOString()
      : product?.linked_installer_details?.drop_off_date;

    const addressId =
      addressSelections[productId] ??
      product?.linked_installer_details?.pickup_address?._id;

    const selectedAddress = addressMap[membershipId]?.find(
      (address) => address._id === addressId
    );

    if (!membershipId) {
      showError("Agent membership ID is missing");
      return;
    }

    try {
      const payload: Record<string, unknown> = {
        supplier_membership_id: supplierMembershipId,
        supplier_name: supplierName,
        location_type: method,
        notes:
          method === "pickup_from_location"
            ? "Please pick up at my location"
            : "",
      };

      if (method === "scheduled_dropoff") {
        payload.drop_off_date = dropOffDate;
      } else {
        payload.address = selectedAddress;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/linked-installer/memberships/${membershipId}/product/${productId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        const errorMessage = data?.error || "Failed to link product.";
        showError(errorMessage);
        alert(errorMessage);
        return;
      }
      showSuccess(data.message || "Product linked successfully!");

      // Send notifications for product link requested
      try {
        const { createNotification } = await import("../../services/notificationApi");
        const { getProductLinkRequestedSignageContent } = await import("../../services/notificationContent");
        const { fetchUserData } = await import("utils/userDataFetcher");
        
        const product = agentProducts?.find((p) => p._id === productId);
        if (product && supplierMembershipId && token) {
          const productName = product?.product?.name || "Product";
          const requesterName = product?.agent_name || "Agent";
          
          // Send notification to supplier (signage)
          try {
            const supplierData = await fetchUserData(supplierMembershipId, token || "");
            if (supplierData) {
              const effectiveSignageId = supplierData.id || supplierMembershipId;
              
              // Fetch all signage team members and send notifications
              const signageTeamResp = await fetch(
                `${process.env.REACT_APP_API_URL}/users/by-supplier/${effectiveSignageId}`,
                {
                  method: "GET",
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                }
              );
              
              if (signageTeamResp.ok) {
                const signageTeamData = await signageTeamResp.json();
                const signageTeamMembers = Array.isArray(signageTeamData?.data) ? signageTeamData.data : [];
                
                for (const member of signageTeamMembers) {
                  const memberId = member.id || member._id;
                  if (memberId && token) {
                    try {
                      const memberLicenseType = member.licenseType || "SIGNAGE_TEAM_MEMBER";
                      const memberRecipientType = memberLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";
                      const content = getProductLinkRequestedSignageContent({
                        agentOrTeamMemberName: requesterName,
                        productName: productName,
                        recipientType: memberRecipientType,
                      });
                      await createNotification(memberId, content, token, memberRecipientType);
                    } catch (memberErr) {
                      console.error(`Failed to send notification to signage team member ${memberId}:`, memberErr);
                    }
                  }
                }
              }
            }
          } catch (signageErr) {
            console.error("Failed to send product link requested notifications to signage:", signageErr);
          }
        }
      } catch (notifError) {
        console.error("Failed to send product link requested notifications:", notifError);
      }

      // Send email notifications for product link requested
      try {
        const { sendTriggerEmails } = await import("utils/emailService");
        const { fetchUserData } = await import("utils/userDataFetcher");
        const from = process.env.REACT_APP_FROM_EMAIL2 || "noreply@realesy.com";

        if (from && supplierMembershipId && product) {
          const productName = product?.product?.name || "Product";
          const requestDate = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
          const requestedBy = product?.agent_name || "Agent";
          const reason = method === "pickup_from_location" ? "Please pick up at my location" : method === "scheduled_dropoff" ? `Scheduled drop-off on ${dropOffDate ? new Date(dropOffDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : "N/A"}` : "Product link requested";

          // Send to Supplier (Signage)
          if (supplierMembershipId) {
            try {
              const supplierData = await fetchUserData(supplierMembershipId, token || "");
              const supplierEmail = supplierData?.email || supplierData?.firmDetails?.email;
              const supplierFirstName = supplierData?.firstName || supplierData?.firmDetails?.firstName || supplierEmail?.split("@")[0] || "Supplier";

              if (supplierEmail) {
                const supplierParams: Record<string, string> = {
                  firstName: supplierFirstName,
                  productName: productName,
                  requestedBy: requestedBy,
                  requestDate: requestDate,
                  reason: reason,
                };

                const supplierLicenseType = supplierData?.licenseType === "SIGNAGE_FIRM" ? "SIGNAGE_FIRM" : "SIGNAGE_TEAM_MEMBER";
                await sendTriggerEmails(
                  "Product link requested",
                  supplierLicenseType,
                  supplierParams,
                  supplierEmail,
                  from,
                  token || ""
                );
              }
            } catch (supplierErr) {
              console.error("Failed to send email to supplier:", supplierErr);
            }
          }

          // Send to Agent (End User)
          if (membershipId) {
            try {
              const agentData = await fetchUserData(membershipId, token || "");
              const agentEmail = agentData?.email || product?.agent_email;
              const agentFirstName = agentData?.firstName || agentEmail?.split("@")[0] || "Agent";

              if (agentEmail) {
                const agentParams: Record<string, string> = {
                  firstName: agentFirstName,
                  productName: productName,
                  supplierName: supplierName || "Supplier",
                  newStatus: "Link Requested",
                  updatedOn: requestDate,
                  supplierComment: reason,
                };

                await sendTriggerEmails(
                  "Product link requested",
                  "AGENT",
                  agentParams,
                  agentEmail,
                  from,
                  token || ""
                );
              }
            } catch (agentErr) {
              console.error("Failed to send email to agent:", agentErr);
            }
          }
        }
      } catch (emailError) {
        console.error("Failed to send product link requested emails:", emailError);
        // Don't block the operation if email fails
      }

      navigate(0);
    } catch (error) {
      console.error("Error linking product:", error);
    }
  };

  const handleUnLink = async (productId: string, membershipId: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/linked-installer/memberships/${membershipId}/product/${productId}/request-unlink`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            product: {
              linked_mode: "unlock_pending",
              locked_status: "locked",
            },
          }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        const errorMessage = data?.error || "Failed to Unlink product.";
        showError(errorMessage);
        alert(errorMessage);
        return;
      }
      showSuccess("Product Unlink requested successfully!");

      // Send notifications for product unlink requested
      try {
        const { createNotification } = await import("../../services/notificationApi");
        const { getProductUnlinkedSignageContent } = await import("../../services/notificationContent");
        const { fetchUserData } = await import("utils/userDataFetcher");
        
        const product = agentProducts?.find((p) => p._id === productId);
        if (product && token) {
          const productName = product?.product?.name || "Product";
          const requesterName = product?.agent_name || "Agent";
          const rawSupplierId =
            (product?.linked_installer_details as any)?.supplier_membership_id ??
            product?.linked_installer_details?.supplier_id;
          const supplierMembershipId = normalizeSupplierVendorId(rawSupplierId);
          
          // Send notification to supplier (signage)
          if (supplierMembershipId) {
            try {
              const supplierData = await fetchUserData(supplierMembershipId, token || "");
              if (supplierData) {
                const effectiveSignageId = supplierData.id || supplierMembershipId;
                
                // Fetch all signage team members and send notifications
                const signageTeamResp = await fetch(
                  `${process.env.REACT_APP_API_URL}/users/by-supplier/${effectiveSignageId}`,
                  {
                    method: "GET",
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                  }
                );
                
                if (signageTeamResp.ok) {
                  const signageTeamData = await signageTeamResp.json();
                  const signageTeamMembers = Array.isArray(signageTeamData?.data) ? signageTeamData.data : [];
                  
                  for (const member of signageTeamMembers) {
                    const memberId = member.id || member._id;
                    if (memberId && token) {
                      try {
                        const memberLicenseType = member.licenseType || "SIGNAGE_TEAM_MEMBER";
                        const memberRecipientType = memberLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";
                        const content = getProductUnlinkedSignageContent({
                          agentOrTeamMemberName: requesterName,
                          productName: productName,
                          recipientType: memberRecipientType,
                        });
                        await createNotification(memberId, content, token, memberRecipientType);
                      } catch (memberErr) {
                        console.error(`Failed to send notification to signage team member ${memberId}:`, memberErr);
                      }
                    }
                  }
                }
              }
            } catch (signageErr) {
              console.error("Failed to send product unlink requested notifications to signage:", signageErr);
            }
          }
        }
      } catch (notifError) {
        console.error("Failed to send product unlink requested notifications:", notifError);
      }

      // Send email notifications for product unlinked
      try {
        const { sendTriggerEmails } = await import("utils/emailService");
        const { fetchUserData } = await import("utils/userDataFetcher");
        const from = process.env.REACT_APP_FROM_EMAIL2 || "noreply@realesy.com";

        const product = agentProducts?.find((p) => p._id === productId);
        if (from && product) {
          const productName = product?.product?.name || "Product";
          const unlinkedOn = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
          const supplierMembershipId = normalizeSupplierVendorId(
            (product?.linked_installer_details as any)?.supplier_membership_id ??
              product?.linked_installer_details?.supplier_id
          );
          const supplierName = suppliers?.find((s) => s.supplier_id === supplierMembershipId)?.name || "Supplier";
          const reason = "Product unlink requested";

          // Send to Supplier (Signage)
          if (supplierMembershipId) {
            try {
              const supplierData = await fetchUserData(supplierMembershipId, token || "");
              const supplierEmail = supplierData?.email || supplierData?.firmDetails?.email;
              const supplierFirstName = supplierData?.firstName || supplierData?.firmDetails?.firstName || supplierEmail?.split("@")[0] || "Supplier";
              // console.log('supplierEmail', supplierEmail);
              if (supplierEmail) {
                const supplierParams: Record<string, string> = {
                  firstName: supplierFirstName,
                  productName: productName,
                  supplierName: supplierName,
                  unlinkedOn: unlinkedOn,
                  reason: reason,
                };

                const supplierLicenseType = supplierData?.licenseType === "SIGNAGE_FIRM" ? "SIGNAGE_FIRM" : "SIGNAGE_TEAM_MEMBER";
                await sendTriggerEmails(
                  "Product Unlinked",
                  supplierLicenseType,
                  supplierParams,
                  supplierEmail,
                  from,
                  token || ""
                );
              }
            } catch (supplierErr) {
              console.error("Failed to send email to supplier:", supplierErr);
            }
          }

          // Send to Agent (End User)
          if (membershipId) {
            try {
              const agentData = await fetchUserData(membershipId, token || "");
              const agentEmail = agentData?.email || product?.agent_email;
              const agentFirstName = agentData?.firstName || agentEmail?.split("@")[0] || "Agent";

              if (agentEmail) {
                const agentParams: Record<string, string> = {
                  firstName: agentFirstName,
                  productName: productName,
                  supplierName: supplierName,
                  unlinkedOn: unlinkedOn,
                  reason: reason,
                };

                await sendTriggerEmails(
                  "Product Unlinked",
                  "AGENT",
                  agentParams,
                  agentEmail,
                  from,
                  token || ""
                );
              }
            } catch (agentErr) {
              console.error("Failed to send email to agent:", agentErr);
            }
          }
        }
      } catch (emailError) {
        console.error("Failed to send product unlinked emails:", emailError);
        // Don't block the operation if email fails
      }

      navigate(0);
    } catch (error) {
      console.error("Error canceling product link:", error);
    }
  };

  const handleCancelLink = async (productId: string, membershipId: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/linked-installer/memberships/${membershipId}/product/${productId}/cancel`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (!response.ok) {
        const errorMessage = data?.error || "Failed to CancelLink product.";
        showError(errorMessage);
        alert(errorMessage);
        return;
      }

      showSuccess("Product Unlink cancelled successfully!");
      navigate(0);
    } catch (error) {
      console.error("Error canceling product link:", error);
    }
  };

  useEffect(() => {
    const rows: ExportRow[] = filteredProducts.map(product => ({
      "Agent Name": product?.agent_name || '',
      "Product Name": product?.product?.name || '',
      "SKU": product?.product?.sku || '',
      "Quantity": product?.product?.variants?.[0]?.inventory?.[0]?.available || 0,
      "Brand": product?.product?.brand || '',
      "Category": product?.product?.category || '',
      "Item Condition": product?.product?.item_condition || '',
      "Status": product?.product?.status ? 'Active' : 'Inactive',
    }));

    setExportRows(rows);
  }, [filteredProducts]);


  const columns = useMemo(() => [
    // columnHelper.display({
    //   id: "serial",
    //   header: () => <div className="th-text S-No">S.No.</div>,
    //   cell: (info) => (
    //     <div className="text-sm S.No">
    //       {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
    //     </div>
    //   ),
    // }),
    columnHelper.display({
      id: "image",
      header: () => <div className="th-text S-No">Image</div>,
      cell: (info) => {
        const raw = (info.row.original?.product as any)?.images?.[0]?.url as string | undefined;
        let url = "";

        if (raw) {
          if (ABS_URL.test(raw)) {
            // already a full URL
            url = raw;
          } else if (isHexId(raw)) {
            // hex id -> resolve via presigned map / helper
            const map: Record<string, string> = (imageUrlMap as any) || {};
            url = map[raw] || getImageSrc(raw, map) || "";
          } else {
            // legacy relative path
            const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
            const path = raw.replace(/^\/+/, "");
            url = `${base}/${encodeURI(path)}`;
          }
        }

        const src = url || imageCustomSquare;

        return (
          <div className="text-sm S-No">
            <img
              src={src}
              alt="Product"
              style={{ width: 50, height: 50, objectFit: "contain", borderRadius: 4 }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = imageCustomSquare;
              }}
            />
          </div>
        );

      },
      enableGlobalFilter: false,
    }),
    columnHelper.accessor(
      (row: AgentProduct) => row.agent_name,
      {
        id: "agent_name",
        header: () => (
          <div className="th-text">
            Agent Name
          </div>
        ),
        cell: info => (
          <div className=" text-sm">
            {info.getValue() || "-"}
          </div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }
    ),

    columnHelper.accessor(
      (row: AgentProduct) => row.agent_email,
      {
        id: "agent_email",
        header: () => (
          <div className="th-text">
            Agent Email
          </div>
        ),
        cell: info => (
          <div className=" text-sm">
            {info.getValue() || "-"}
          </div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }
    ),
    columnHelper.accessor("product.name", {
      header: () => <div className="th-text">Product Name</div>,
      cell: (info) => (
        <div className="text-sm wrap-anywhere">{info.getValue()}</div>
      ),
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),
    columnHelper.accessor("product.category", {
      header: () => <div className="th-text">Category</div>,
      cell: (info) => (
        <div className="text-sm wrap-anywhere">{info.getValue()}</div>
      ),
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),
    columnHelper.accessor(
      (row: any) => row.product?.variants?.[0]?.inventory?.[0]?.on_hand,
      {
        id: "on_hand",
        header: () => <div className="th-text">Qty</div>,
        cell: (info) => (
          <div className="text-sm">{info.getValue() ?? "-"}</div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }
    ),
    columnHelper.accessor(
      (row) => row?.product?.item_condition,
      {
        id: "item_condition",
        header: () => <div className="th-text">Condition</div>,
        cell: (info) => (
          <div className="text-sm">{info.getValue()}</div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
    columnHelper.accessor(
      (row: any) => row._id,
      {
        id: "productLocationStatus",
        header: () => (
          <div className="th-text">Handover Method</div>
        ),
        cell: ({ row }) => {
          const agentproduct = row.original;
          const productId = agentproduct._id;
          const linkedDetails = agentproduct.linked_installer_details;
          const isLocked = agentproduct?.product?.locked_status === "locked";
          const isDisabled =
            isLocked || agentproduct?.product?.current_status === "in_transit";

          const selectedMethod =
            methodSelections[productId] ??
            agentproduct?.product?.product_location_status;

          // ✅ Locked → Only show Supplier Select (disabled)
          if (isLocked) {
            return (
              <div className="text-sm">
                <select
                  className="form-control mb-2 supplier-select-r form-select po-select"
                  value={
                    supplierSelections[productId] ??
                    normalizeSupplierVendorId(
                      (linkedDetails as any)?.supplier_membership_id ?? linkedDetails?.supplier_id
                    ) ??
                    ""
                  }
                  onChange={(e) => handleSupplierChange(productId, e.target.value)}
                  disabled={true}
                >
                  <option value="">- Select Supplier -</option>
                  {suppliers?.map((supplier) => (
                    <option key={supplier.supplier_id} value={supplier.supplier_id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </div>
            );
          }

          // ✅ Otherwise → Full UI
          return (
            <div className="text-sm">
              {/* Supplier Select */}
              <select
                className="form-control mb-2 supplier-select-r form-select po-select"
                value={
                  supplierSelections[productId] ??
                  normalizeSupplierVendorId(
                      (linkedDetails as any)?.supplier_membership_id ?? linkedDetails?.supplier_id
                    ) ??
                  ""
                }
                onChange={(e) => handleSupplierChange(productId, e.target.value)}
                disabled={isDisabled}
              >
                <option value="">- Select Supplier -</option>
                {suppliers?.map((supplier) => (
                  <option key={supplier.supplier_id} value={supplier.supplier_id}>
                    {supplier.name}
                  </option>
                ))}
              </select>

              {/* Method Select */}
              <select
                className="form-control mb-2 supplier-select-r form-select po-select"
                value={selectedMethod ?? ""}
                onChange={(e) =>
                  handleMethodChange(productId, e.target.value, getAgentMembershipId(agentproduct))
                }
                disabled={isDisabled}
              >
                <option value="">- Select Method -</option>
                <option value="scheduled_dropoff">Scheduled for Drop-off</option>
                <option value="pickup_from_location">Pickup from my location</option>
              </select>

              {/* Date or Address Select */}
              {selectedMethod === "scheduled_dropoff" ? (
                <input
                  type="date"
                  className="form-control mb-2 supplier-select-r form-select po-select"
                  min={new Date().toISOString().split("T")[0]}
                  value={
                    dateSelections[productId] ??
                    (linkedDetails?.drop_off_date
                      ? new Date(linkedDetails.drop_off_date)
                        .toISOString()
                        .split("T")[0]
                      : "")
                  }
                  onChange={(e) => handleDateChange(productId, e.target.value)}
                  disabled={isDisabled}
                />
              ) : (
                <select
                  className="form-control mb-2 supplier-select-r form-select po-select"
                  value={
                    addressSelections[productId] ??
                    linkedDetails?.pickup_address?._id ??
                    ""
                  }
                  onChange={(e) => handleAddressChange(productId, e.target.value)}
                  disabled={isDisabled}
                >
                  <option value="">- Select Location -</option>
                  {(addressMap[getAgentMembershipId(agentproduct)] || []).map((address) => (
                    <option key={address._id} value={address._id}>
                      {address.label}
                    </option>
                  ))}
                </select>
              )}

              {/* Action Button */}
              <div className="text-center">
                <button
                  className="btn btn-success confirm-btn text-center"
                  onClick={() =>
                    handleSubmitLink(productId, getAgentMembershipId(agentproduct))
                  }
                  disabled={isDisabled}
                >
                  {linkedDetails ? "Update" : "Confirm"}
                </button>
              </div>
            </div>
          );
        },
        enableGlobalFilter: false,
      }
    ),
    // columnHelper.accessor(
    //   (row: any) => row._id,
    //   {
    //     id: "productLocationStatus",
    //     header: () => <div className="th-text">Handover Method</div>,
    //     cell: ({ row }) => {
    //       const productId = row.original._id;
    //       const linkedDetails = row.original.linked_installer_details;
    //       const isDisabled =
    //         row.original.product?.locked_status === "locked" ||
    //         linkedDetails?.current_status === "in_transit";
    //       const selectedMethod =
    //         methodSelections[productId] ?? linkedDetails?.product_location_status;
    //       let agentproduct = row?.original;

    //       return (
    //         <div className="text-sm">
    //           {/* Supplier Select */}
    //           <select
    //             className="form-control mb-2 supplier-select-r form-select po-select"
    //             value={supplierSelections[agentproduct._id] ?? agentproduct?.linked_installer_details?.supplier_id ?? ""}
    //             onChange={(e) => handleSupplierChange(agentproduct._id, e.target.value)}
    //             disabled={
    //               agentproduct?.product?.locked_status === "locked" ||
    //               agentproduct?.product?.current_status === "in_transit"
    //             }
    //           >
    //             <option value="">- Select Supplier -</option>
    //             {suppliers?.map((supplier) => (
    //               <option key={supplier.supplier_id} value={supplier.supplier_id}>
    //                 {supplier.name}
    //               </option>
    //             ))}
    //           </select>

    //           {/* Method Select */}
    //           <select
    //             className="form-control mb-2 supplier-select-r form-select po-select"
    //             value={methodSelections[agentproduct._id] ?? agentproduct?.product?.product_location_status ?? ""}
    //             onChange={(e) =>
    //               handleMethodChange(agentproduct._id, e.target.value, agentproduct.agent_id)
    //             }
    //             disabled={
    //               agentproduct?.product?.locked_status === "locked" ||
    //               agentproduct?.product?.current_status === "in_transit"
    //             }
    //           >
    //             <option value="">- Select Method -</option>
    //             <option value="scheduled_dropoff">Scheduled for Drop-off</option>
    //             <option value="pickup_from_location">Pickup from my location</option>
    //             <option value="at_installation_site">At installation site</option>
    //           </select>

    //           {/* Conditional date or address select */}
    //           {(methodSelections[agentproduct._id] ??
    //             agentproduct?.product?.product_location_status) === "scheduled_dropoff" ? (
    //             <input
    //               disabled={
    //                 agentproduct?.product?.locked_status === "locked" ||
    //                 agentproduct?.product?.current_status === "in_transit"
    //               }
    //               type="date"
    //               className="form-control mb-2 supplier-select-r form-select po-select"
    //               value={
    //                 dateSelections[agentproduct._id] ??
    //                 (agentproduct?.linked_installer_details?.drop_off_date
    //                   ? new Date(agentproduct.linked_installer_details.drop_off_date)
    //                     .toISOString()
    //                     .split("T")[0]
    //                   : "")
    //               }
    //               onChange={(e) => handleDateChange(agentproduct._id, e.target.value)}
    //             />
    //           ) : (
    //             <select
    //               className="form-control mb-2 supplier-select-r form-select po-select"
    //               value={addressSelections[agentproduct._id] ?? agentproduct?.linked_installer_details?.pickup_address?._id ?? ""}
    //               onChange={(e) =>
    //                 handleAddressChange(agentproduct._id, e.target.value)
    //               }
    //               disabled={
    //                 agentproduct?.product?.locked_status === "locked" ||
    //                 agentproduct?.product?.current_status === "in_transit"
    //               }
    //             >
    //               <option value="">- Select Location -</option>
    //               {(addressMap[agentproduct.agent_id] || []).map((address) => (
    //                 <option key={address._id} value={address._id}>
    //                   {address.label}
    //                 </option>
    //               ))}
    //             </select>
    //           )}
    //           <div className="">
    //             <button
    //               className="btn btn-success"
    //               onClick={() =>
    //                 handleSubmitLink(agentproduct._id, agentproduct.agent_id)
    //               }
    //               disabled={
    //                 agentproduct?.product?.locked_status === "locked" ||
    //                 agentproduct?.product?.current_status === "in_transit"
    //               }
    //             >
    //               {agentproduct?.linked_installer_details ? "Update" : "Confirm"}
    //             </button>
    //           </div>
    //         </div>
    //       );
    //     },
    //     enableGlobalFilter: false,
    //   }
    // ),
    // columnHelper.accessor(
    //   (row: AgentProduct) => (
    //     <span className="fw-bold text-capitalize text-info">
    //       {row?.product?.current_status && row.product.current_status !== "none"
    //         ? row.product.current_status.replace(/_/g, " ")
    //         : "-"}
    //     </span>
    //   ),
    //   {
    //     id: "current_status",
    //     header: () => <div className="th-text">Product location status</div>,
    //     cell: (info) => <div className="text-sm">{info.getValue()}</div>,
    //     filterFn: fuzzyTextFilter,
    //     enableGlobalFilter: true,
    //   }
    // ),
    columnHelper.accessor(
      (row: AgentProduct) =>
        row.product.current_status?.replace(/_/g, " ") || "-",
      {
        id: "current_status",
        header: () => (
          <div className="th-text">
            Product location status
          </div>
        ),
        cell: info => (
          <div className=" text-sm">
            <StatusBadge status={info.getValue()} />
          </div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }
    ),
    columnHelper.accessor(
      (row: AgentProduct) => (
        <span className="linked-status">
          {row?.product?.linked_mode === "unlock_pending"
            ? "Unlink Pending"
            : row?.product?.locked_status === "unlocked"
              ? ""
              : "Linked"}
        </span>
      ),
      {
        id: "linked_status",
        header: () => <div className="th-text">Linked Status</div>,
        cell: (info) => <div className="text-sm linked-status">{info.getValue()}</div>,
        enableGlobalFilter: true,
      }
    ),
    columnHelper.accessor(
      (row: any) => row.linked_installer_details?.supplier_name || "",
      {
        id: "linked_supplier",
        header: () => <div className="th-text">Linked Supplier</div>,
        cell: (info) => (
          <div className="text-sm">{info.getValue()}</div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

    columnHelper.display({
      id: "actions",
      header: () => <div className="th-text">Actions</div>,
      cell: (info) => {
        const agentproduct = info.row.original;
        return (
          <div className="text-sm d-flex justify-content-center gap-2">
            {canView && (
              <ViewIconButton
                to={`${baseRoute}/agent/${getAgentMembershipId(agentproduct)}/viewAgentProduct/${agentproduct._id}`}
                className="btn action-btn fs-2 "
                title="View"
              />
            )}

            {
              canEdit &&
              !(agentproduct?.product?.locked_status === "locked" ||
                agentproduct?.linked_installer_details?.current_status === "in_transit") &&
              <EditIconButton
                to={`${baseRoute}/agent/${getAgentMembershipId(agentproduct)}/editAgentProduct/${agentproduct._id}`}
                className="icon-square"
                title="Edit"
              />
            }
            {agentproduct?.product?.locked_status === "unlocked" ? (
              canDelete && (
                <DeleteIconButton
                  onClick={() => handleDeleteClick(agentproduct)}
                  className="icon-square danger"
                  title="Delete"
                  disabled={deleting === agentproduct._id}
                />
              )
            ) : (
              <UnlinkIconButton
                onClick={() => handleUnLink(agentproduct?._id, getAgentMembershipId(agentproduct))}
                className="btn btn-danger btn-sm"

                title="Unlink"
                aria-label="Unlink"
                disabled={agentproduct?.product?.linked_mode === "unlock_pending"}
              />

            )}
            {(agentproduct?.product?.current_status === "pending_dropoff" ||
              agentproduct?.product?.current_status === "awaiting_response") && (
                <CancelIconButton
                  className="btn btn-sm btn-danger"
                  onClick={() =>
                    handleCancelLink(agentproduct?._id, getAgentMembershipId(agentproduct))
                  }
                />
              )}
          </div>
        );
      },
    }),
  ], [
    deleting,
    suppliers,
    supplierSelections,
    methodSelections,
    addressSelections,
    dateSelections,
    handleSupplierChange,
    handleMethodChange,
    handleAddressChange,
    handleDateChange,
    handleSubmitLink,
    canView,
    canEdit,
    canDelete,
  ]);


  const table = useReactTable({
    data: agentProducts,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  return (
    <div className="content">
      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
        <h4 className="mb-0"></h4>
        <div className="d-flex gap-3 align-items-center">
          {canCreate && (
            <Link to={`${baseRoute}/addAgentProduct`} className="btn btn-primary  table-addbutton">
              + Add Agent Product
            </Link>
          )}

        </div>
      </div>

      <div className="po-header mb-4 shadow-border-datatables ">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div>
            <h4 className="po-title mb-1">Agent Inventory</h4>
            <div className="po-subtitle">Manage and link your Agent products with suppliers</div>
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Product Name or SKU"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setGlobalFilter(searchInput);
                  }
                }}
              />
            </div>
            <ExportAsExcel
              data={exportRows}
              headers={[
                "Agent Name",
                "Product Name",
                "SKU",
                "Quantity",
                "Brand",
                "Category",
                "Item Condition",
                "Status"
              ]}
              fileName="AgentInventory"
            >
              {(props) => (
                <button {...props} className="export-button">
                  <i className="ri-file-excel-2-line me-2" />
                  Export to Excel
                </button>
              )}
            </ExportAsExcel>
          </div>
        </div>
      </div>


      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header like screenshot (left title, right count pill) */}
        <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
          <div className="d-flex align-items-center">
            <i
              className="bi bi-box-seam me-2"
              aria-hidden="true"
              style={{ fontSize: 18, color: "#2563eb", lineHeight: 1 }}
            />
            <h5 className="mb-0">Products</h5>
          </div>
          <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
            {/* {products.length} orders */}
          </span>
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts table={table} />
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="product_name"
            actionColId="actions"
          />
        </div>

      </div>
      {table.getRowModel().rows.length === 0 ? (
        <div className=" my-4 text-muted" style={{ fontSize: "1.1rem" }}>
          No records found
        </div>
      ) : (
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      )}

      <DeleteConfirmModal
        open={!!deleteTarget}
        dynamicName={deleteTarget?.name || "Product"}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
      />

      <style>{`
        /* Hide dropdown arrow for disabled select fields */
        select.form-select:disabled,
        select.po-select:disabled,
        select.supplier-select-r:disabled {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          background-image: none !important;
          padding-right: 12px;
        }
      `}</style>
    </div>
  );
};

export default RealtorAgentInventory;




// const columns = useMemo(() => {
//   return [
//     columnHelper.display({
//       id: "serial",
//       header: "S.No.",
//       cell: (info) => pagination.pageIndex * pagination.pageSize + info.row.index + 1,
//     }),
//     columnHelper.accessor("product.image.url", {
//       header: "Image",
//       cell: (info) => {
//         const imageUrl = info.getValue();
//         return imageUrl ? (
//           <img
//             src={`${process.env.REACT_APP_BASE_URL}${imageUrl}`}
//             height={50}
//             alt="Product"
//           />
//         ) : (
//           <span>No image</span>
//         );
//       },
//     }),
//     columnHelper.accessor("product.name", {
//       header: "Product Name",
//       filterFn: fuzzyTextFilter,
//       enableGlobalFilter: true,
//     }),
//     columnHelper.accessor("product.category", {
//       header: "Category",
//       filterFn: fuzzyTextFilter,
//       enableGlobalFilter: true,
//     }),
//     columnHelper.accessor(
//       (row: any) => row.product?.variants?.[0]?.inventory?.[0]?.on_hand,
//       {
//         id: "on_hand",
//         header: "Qty",
//         // cell: (info) =>
//         //   info.row.original.product?.variants?.[0]?.inventory?.[0]?.on_hand ?? "-",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }
//     ),
//     columnHelper.accessor(
//       (row) => row?.product?.item_condition,
//       {
//         header: "Condition",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: any) => row.linked_installer_details?.supplier_name || "",
//       {
//         header: "Linked Supplier",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: any) => row._id, // or another meaningful base value
//       {
//         id: "productLocationStatus", // important when using function accessor
//         header: "Product Locations Status",
//         cell: ({ row }) => {
//           const productId = row.original._id;
//           const linkedDetails = row.original.linked_installer_details;
//           const isDisabled =
//             row.original.product?.locked_status === "locked" ||
//             linkedDetails?.current_status === "in_transit";

//           const selectedMethod =
//             methodSelections[productId] ??
//             linkedDetails?.product_location_status;
//           let agentproduct = row?.original;

//           return (
//             <>
//               {/* Supplier Select */}
//               <select
//                 className="form-control mb-2 supplier-select-r form-select po-select"
//                 value={supplierSelections[agentproduct._id] ?? agentproduct?.linked_installer_details?.supplier_id ?? ""}
//                 onChange={(e) => handleSupplierChange(agentproduct._id, e.target.value)}
//                 disabled={
//                   agentproduct?.product?.locked_status ===
//                   "locked" ||
//                   agentproduct?.linked_installer_details
//                     ?.current_status === "in_transit"
//                 }
//               >
//                 <option value="">- Select Supplier -</option>
//                 {suppliers?.map((supplier) => (
//                   <option
//                     key={supplier.supplier_id}
//                     value={supplier.supplier_id}
//                   >
//                     {supplier.name}
//                   </option>
//                 ))}
//               </select>

//               {/* Method Select */}
//               <select
//                 className="form-control mb-2 supplier-select-r form-select po-select"
//                 value={methodSelections[agentproduct._id] ?? agentproduct?.linked_installer_details?.product_location_status ?? ""}
//                 onChange={(e) =>
//                   handleMethodChange(
//                     agentproduct._id,
//                     e.target.value,
//                     agentproduct.agent_id
//                   )
//                 }
//                 disabled={
//                   agentproduct?.product?.locked_status ===
//                   "locked" ||
//                   agentproduct?.linked_installer_details
//                     ?.current_status === "in_transit"
//                 }
//               >
//                 <option value="">- Select Method -</option>
//                 <option value="scheduled_dropoff">
//                   Scheduled for Drop-off
//                 </option>
//                 <option value="pickup_from_location">
//                   Pickup from my location
//                 </option>
//                 <option value="at_installation_site">
//                   At installation site
//                 </option>
//               </select>

//               {/* Conditional date or address select */}
//               {(methodSelections[agentproduct._id] ??
//                 agentproduct?.linked_installer_details
//                   ?.product_location_status) ===
//                 "scheduled_dropoff" ? (
//                 <input
//                   disabled={
//                     agentproduct?.product?.locked_status ===
//                     "locked" ||
//                     agentproduct?.linked_installer_details
//                       ?.current_status === "in_transit"
//                   }
//                   type="date"
//                   className="form-control mb-2 supplier-select-r form-select po-select"
//                   value={dateSelections[agentproduct._id] ?? (agentproduct?.linked_installer_details?.drop_off_date ? new Date(agentproduct.linked_installer_details.drop_off_date).toISOString().split("T")[0] : "")}
//                   onChange={(e) =>
//                     handleDateChange(
//                       agentproduct._id,
//                       e.target.value
//                     )
//                   }
//                 />
//               ) : (
//                 <select
//                   className="form-control mb-2 supplier-select-r form-select po-select"
//                   value={addressSelections[agentproduct._id] ?? agentproduct?.linked_installer_details?.pickup_address?._id ?? ""}
//                   onChange={(e) =>
//                     handleAddressChange(
//                       agentproduct._id,
//                       e.target.value
//                     )
//                   }
//                   disabled={
//                     agentproduct?.product?.locked_status ===
//                     "locked" ||
//                     agentproduct?.linked_installer_details
//                       ?.current_status === "in_transit"
//                   }
//                 >
//                   <option value="">
//                     - Select Location -
//                   </option>

//                   {(
//                     addressMap[agentproduct.agent_id] || []
//                   ).map((address) => (
//                     <option
//                       key={address._id}
//                       value={address._id}
//                     >
//                       {address.label}
//                     </option>
//                   ))}
//                 </select>
//               )}
//               <button
//                 className="btn btn-success w-100"
//                 onClick={() =>
//                   handleSubmitLink(
//                     agentproduct._id,
//                     agentproduct.agent_id
//                   )
//                 }
//                 disabled={
//                   agentproduct?.product?.locked_status ===
//                   "locked" ||
//                   agentproduct?.linked_installer_details
//                     ?.current_status === "in_transit"
//                 }
//               >
//                 {agentproduct?.linked_installer_details
//                   ? "Update"
//                   : "Confirm"}
//               </button>
//             </>
//           );
//         },
//         enableGlobalFilter: false, // ✅ FIXED: added comma
//       }
//     ),
//     columnHelper.accessor(
//       (row: AgentProduct) => (
//         <span className="fw-bold text-capitalize text-info">
//           {row?.product?.current_status &&
//             row.product.current_status !== "none"
//             ? row.product.current_status.replace(/_/g, " ") : "-"}</span>),
//       {
//         id: "current_status",
//         header: "Current Status",
//         cell: (info) => info.getValue(),
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }
//     ),
//     columnHelper.accessor((row: AgentProduct) => (
//       <span className="text-success fw-bold">
//         {row?.product?.linked_mode === "unlock_pending"
//           ? "Unlink Pending"
//           : row?.product?.locked_status === "unlocked"
//             ? ""
//             : "Linked"}</span>),
//       {
//         header: "Linked Status",
//         cell: (info) => info.getValue(),
//         enableGlobalFilter: true,
//       }),

//     columnHelper.display({
//       id: "actions",
//       header: "Actions",
//       cell: (info) => {
//         const agentproduct = info.row.original;
//         return (
//           <>
//             <Link
//               to={`${baseRoute}/agent/${agentproduct.agent_id}/viewAgentProduct/${agentproduct._id}`}
//               className="view me-3"
//             >
//               <i className="ri-eye-fill fs-20"></i>
//             </Link>
//             {
//               !(agentproduct?.product?.locked_status ===
//                 "locked" ||
//                 agentproduct?.linked_installer_details
//                   ?.current_status === "in_transit")
//               &&
//               <Link
//                 to={`${baseRoute}/agent/${agentproduct.agent_id}/editAgentProduct/${agentproduct._id}`}
//                 className="edit me-3"
//               >
//                 <i className="ri-pencil-fill fs-20"></i>
//               </Link>
//             }
//             {agentproduct?.product?.locked_status ===
//               "unlocked" ? (
//               <button
//                 onClick={() =>
//                   handleDelete(
//                     agentproduct.agent_id,
//                     agentproduct._id
//                   )
//                 }
//                 className="del border-0 p-0"
//               >
//                 <i className="ri-delete-bin-fill fs-18"></i>
//               </button>
//             ) : (
//               <button
//                 onClick={() =>
//                   handleUnLink(
//                     agentproduct?._id,
//                     agentproduct.agent_id
//                   )
//                 }
//                 className="btn btn-danger btn-sm"
//                 disabled={
//                   agentproduct?.product?.linked_mode ===
//                   "unlock_pending"
//                 }
//               >
//                 Unlink
//               </button>
//             )}
//             {(agentproduct?.product?.current_status ===
//               "pending_dropoff" ||
//               agentproduct?.product?.current_status ===
//               "awaiting_response") && (
//                 <button
//                   className="btn btn-sm btn-danger"
//                   onClick={() =>
//                     handleCancelLink(
//                       agentproduct?._id,
//                       agentproduct.agent_id
//                     )
//                   }
//                 >
//                   Cancel
//                 </button>
//               )}
//           </>
//         );
//       },
//     }),
//   ];
// }, [
//   deleting,
//   suppliers,
//   supplierSelections,
//   methodSelections,
//   addressSelections,
//   dateSelections,
//   handleSupplierChange,
//   handleMethodChange,
//   handleAddressChange,
//   handleDateChange,
//   handleSubmitLink,
// ]);